/**
 * LeadsFinder — Apify "Leads Finder" ($1.5/1k, Apollo alternative).
 * Iter330 · Every filter is a searchable multi-select combo backed by the
 * shared /api/taxonomy source of truth.  Enrich CRM tab gets soft delete
 * (individual + bulk), filter dropdowns, batch enrich 10/20/N.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Search, Save, Sparkles, UserSearch, Linkedin, Trash2, Mail,
  AlertTriangle, ChevronRight, ChevronDown, Tag, Wand2,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import MultiSelectCombo from "../components/MultiSelectCombo";
import ExportButton from "../components/ExportButton";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS_KEY = "alp.leadsfinder.filters";

const Skeleton = () => (
  <div className="space-y-2 animate-pulse" data-testid="lf-skeleton">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="h-14 rounded-lg bg-[var(--al-border)]/40" />
    ))}
  </div>
);

// Shared taxonomy loader — cached at module scope so tabs reuse it.
function useTaxonomy() {
  const [tax, setTax] = useState({ cities: [], industries: [], job_titles: [] });
  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/taxonomy`);
      setTax(data || { cities: [], industries: [], job_titles: [] });
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const addNew = async (kind, label) => {
    await axios.post(`${API}/taxonomy/add`, { kind, label });
    await refresh();
  };
  return { tax, refresh, addNew };
}

export default function LeadsFinder() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tab, setTab] = useState("search");
  const auth = useAuth();
  const isSuperAdmin = ["super_admin", "master"].includes(auth?.user?.role);

  if (auth?.user && !isSuperAdmin) {
    return (
      <div className="flex min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="leads-finder-page">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="al-card p-8 max-w-md text-center" data-testid="lf-restricted">
            <UserSearch className="w-8 h-8 text-[#C9A24E] mx-auto mb-3" />
            <h2 className="text-[16px] font-extrabold mb-1">{isPt ? "Acesso restrito" : "Restricted access"}</h2>
            <p className="text-[12.5px] text-[var(--al-text-muted)]">
              {isPt ? "Disponível apenas para administradores master." : "Master admins only."}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="leads-finder-page">
      <Sidebar />
      <main className="flex-1 min-w-0 px-6 py-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <header className="mb-4">
            <h1 className="text-[22px] font-extrabold inline-flex items-center gap-2">
              <UserSearch className="w-6 h-6 text-[#C9A24E]" /> Leads Finder
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">
                $1.5/1k · verified emails
              </span>
            </h1>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">
              {isPt
                ? "Alternativa ao Apollo via Apify — busque decisores ou enriqueça leads do CRM"
                : "Apollo alternative via Apify — find decision-makers or enrich CRM leads"}
            </p>
          </header>

          <div className="flex gap-2 border-b border-[var(--al-border)] mb-5">
            {[
              { id: "search", label: isPt ? "🔍 Buscar novos leads" : "🔍 Find new leads" },
              { id: "enrich", label: isPt ? "✨ Enriquecer CRM" : "✨ Enrich CRM" },
            ].map((t) => (
              <button key={t.id} type="button" data-testid={`lf-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition ${
                  tab === t.id ? "border-[#C9A24E]" : "border-transparent text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "search" ? <SearchTab isPt={isPt} /> : <EnrichTab isPt={isPt} />}
        </div>
      </main>
    </div>
  );
}

// ── Find new leads tab ────────────────────────────────────────────────

function SearchTab({ isPt }) {
  const { tax, addNew } = useTaxonomy();
  const persisted = safeGet(FILTERS_KEY, {});
  const [titles, setTitles] = useState(persisted.titles || ["Owner"]);
  const [cities, setCities] = useState(persisted.cities || []);
  const [industries, setIndustries] = useState(persisted.industries || []);
  const [limit, setLimit] = useState(persisted.limit || 25);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    safeSet(FILTERS_KEY, { titles, cities, industries, limit });
  }, [titles, cities, industries, limit]);

  const clearFilters = () => {
    setTitles(["Owner"]); setCities([]); setIndustries([]); setLimit(25);
  };

  const run = async () => {
    if (!titles.length && !industries.length && !cities.length) {
      toast.info(isPt ? "Selecione pelo menos um filtro" : "Add at least one filter");
      return;
    }
    setLoading(true); setItems([]); setSelected(new Set());
    try {
      const { data } = await axios.post(`${API}/leads-finder/search`, {
        job_titles: titles.map((t) => t.toLowerCase()),
        cities,
        company_keywords: industries,
        verified_only: true, limit,
      });
      setItems(data.items || []);
      setSelected(new Set((data.items || []).map((x) => x.id)));
      toast.success(isPt ? `${data.count} leads encontrados` : `${data.count} leads found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const save = async () => {
    const chosen = items.filter((x) => selected.has(x.id));
    if (!chosen.length) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/leads-finder/save`, { items: chosen });
      toast.success(isPt
        ? `${data.saved} salvos · ${data.duplicates} duplicados ignorados`
        : `${data.saved} saved · ${data.duplicates} duplicates skipped`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="al-card p-4 grid md:grid-cols-4 gap-4 mb-3">
        <MultiSelectCombo
          label={isPt ? "Cargos" : "Job titles"}
          options={(tax.job_titles || []).map((t) => ({ label: t.label }))}
          values={titles} onChange={setTitles}
          onAddNew={(v) => addNew("job_title", v)}
          isPt={isPt} testid="lf-titles"
        />
        <MultiSelectCombo
          label={isPt ? "Cidades" : "Cities"}
          options={(tax.cities || []).map((c) => ({ label: c.label, count: c.count }))}
          values={cities} onChange={setCities}
          onAddNew={(v) => addNew("city", v)}
          isPt={isPt} testid="lf-cities"
        />
        <MultiSelectCombo
          label={isPt ? "Segmentos" : "Industries"}
          options={(tax.industries || []).map((i) => ({ label: i.label, count: i.count }))}
          values={industries} onChange={setIndustries}
          onAddNew={(v) => addNew("industry", v)}
          isPt={isPt} testid="lf-industries"
        />
        <div>
          <label className="text-[10.5px] font-bold uppercase text-[var(--al-text-muted)] block mb-1">
            {isPt ? "Limite" : "Limit"}
          </label>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
            className="al-input h-9 text-[12px] w-full" data-testid="lf-limit">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} leads</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <button type="button" onClick={run} disabled={loading} data-testid="lf-search-btn"
          className="h-10 px-4 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? (isPt ? "Buscando..." : "Searching...") : (isPt ? "Buscar" : "Search")}
        </button>
        <button type="button" onClick={clearFilters} data-testid="lf-clear-filters"
          className="h-10 px-3 rounded-lg border border-[var(--al-border)] text-[12px] hover:bg-[var(--al-card)]">
          {isPt ? "Limpar filtros" : "Clear filters"}
        </button>
      </div>

      {loading && <Skeleton />}

      {!loading && items.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] text-[var(--al-text-muted)]">
              {selected.size}/{items.length} {isPt ? "selecionados" : "selected"}
            </span>
            <button type="button" onClick={save} disabled={saving || !selected.size}
              data-testid="lf-save-btn"
              className="h-9 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isPt ? "Salvar no CRM" : "Save to CRM"}
            </button>
          </div>
          <div className="al-card overflow-x-auto" data-testid="lf-results">
            <table className="w-full text-[12px] min-w-[820px]">
              <thead>
                <tr className="border-b border-[var(--al-border)] text-left text-[10px] uppercase text-[var(--al-text-muted)]">
                  <th className="px-3 py-2.5 w-8" />
                  <th className="px-3 py-2.5">{isPt ? "Contato" : "Contact"}</th>
                  <th className="px-3 py-2.5">{isPt ? "Empresa" : "Company"}</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">LinkedIn</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} data-testid={`lf-row-${i}`}
                    className="border-b border-[var(--al-border)] last:border-0 hover:bg-[var(--al-bg)]">
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={selected.has(it.id)}
                        onChange={() => {
                          const next = new Set(selected);
                          next.has(it.id) ? next.delete(it.id) : next.add(it.id);
                          setSelected(next);
                        }}
                        className="accent-[#C9A24E]" />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold">{it.full_name}</div>
                      <div className="text-[10.5px] text-[var(--al-text-muted)]">{it.job_title}</div>
                    </td>
                    <td className="px-3 py-2.5 text-[var(--al-text-muted)]">
                      {it.company_name}
                      {it.city && <div className="text-[10px]">{it.city}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-[var(--al-text-muted)]">{it.email || "—"}</td>
                    <td className="px-3 py-2.5">
                      {it.linkedin && <a href={it.linkedin} target="_blank" rel="noreferrer"
                        className="text-[#1B3A8A]"><Linkedin className="w-3.5 h-3.5" /></a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Enrich CRM tab ────────────────────────────────────────────────────

function EnrichTab({ isPt }) {
  const { tax } = useTaxonomy();
  const [cityFilter, setCityFilter] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState({});
  const [enriching, setEnriching] = useState(false);
  const [progress, setProgress] = useState({ done: 0, target: 0 });
  const [customCount, setCustomCount] = useState(30);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Iter331 · group-by toggle for LinkedIn Contacts view.
  const [groupBy, setGroupBy] = useState("none"); // none | city | industry | company
  const [groups, setGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (cityFilter[0]) params.city = cityFilter[0];
      if (industryFilter[0]) params.industry = industryFilter[0];
      if (companyFilter) params.company = companyFilter;
      const { data } = await axios.get(`${API}/leads-finder/enrichable`, { params });
      setLeads(data.items || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }, [cityFilter, industryFilter, companyFilter]);
  useEffect(() => { load(); }, [load]);

  // Iter331 · Fetch grouped payload for LinkedIn Contacts view.
  const loadGroups = useCallback(async () => {
    if (groupBy === "none") return;
    setGroupsLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/leads-finder/enrichable/grouped`,
        { params: { by: groupBy, limit_per_group: 100, total_limit: 1000 } },
      );
      setGroups(data.groups || []);
      // Auto-expand the biggest group.
      const first = (data.groups || [])[0];
      setOpenGroups(first ? { [first.key]: true } : {});
    } catch { toast.error("Grouped load failed"); }
    finally { setGroupsLoading(false); }
  }, [groupBy]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  // Iter331 · Inline reclassify — updates row in memory and calls backend.
  const reclassify = async (lead, newSegment) => {
    const from = lead.sector || lead.industry || "";
    if (!newSegment || newSegment === from) return;
    // Optimistic update on flat list.
    setLeads((prev) => prev.map((l) => l.id === lead.id
      ? { ...l, sector: newSegment, industry: newSegment } : l));
    // Optimistic update on groups list.
    setGroups((prev) => prev.map((g) => ({
      ...g,
      items: g.items.map((l) => l.id === lead.id
        ? { ...l, sector: newSegment, industry: newSegment } : l),
    })));
    try {
      await axios.patch(`${API}/leads-finder/leads/${lead.id}/reclassify`,
                        { segment: newSegment });
      toast.success(isPt
        ? `${lead.name}: ${from || "—"} → ${newSegment}`
        : `${lead.name}: ${from || "—"} → ${newSegment}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reclassify failed");
      await load();
      if (groupBy !== "none") await loadGroups();
    }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const enrichBatch = async (n) => {
    const ids = selected.size > 0
      ? [...selected].slice(0, n)
      : leads.slice(0, n).map((l) => l.id);
    if (!ids.length) return;
    const costUsd = (ids.length * 0.0015).toFixed(2);
    if (!window.confirm(isPt
      ? `Enriquecer ${ids.length} leads? Custo estimado: US$${costUsd}.`
      : `Enrich ${ids.length} leads? Estimated cost: US$${costUsd}.`)) return;
    setEnriching(true); setProgress({ done: 0, target: ids.length });
    try {
      const { data } = await axios.post(`${API}/leads-finder/enrich`, {
        lead_ids: ids, max_items: ids.length,
      });
      const map = {};
      (data.results || []).forEach((r) => { map[r.lead_id] = r; });
      setResults((prev) => ({ ...prev, ...map }));
      setProgress({ done: data.enriched, target: ids.length });
      toast.success(isPt
        ? `${data.enriched}/${ids.length} enriquecidos`
        : `${data.enriched}/${ids.length} enriched`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrich failed");
    } finally { setEnriching(false); }
  };

  const softDeleteOne = async (lead) => {
    setLeads((prev) => prev.filter((x) => x.id !== lead.id));
    try {
      await axios.delete(`${API}/leads-finder/leads/${lead.id}`);
      toast.success(isPt ? `${lead.name} excluído` : `${lead.name} deleted`, {
        duration: 10000,
        action: {
          label: isPt ? "Desfazer" : "Undo",
          onClick: async () => {
            await axios.post(`${API}/leads-finder/leads/${lead.id}/restore`);
            await load();
          },
        },
      });
      setTotal((t) => Math.max(0, t - 1));
    } catch { toast.error("Delete failed"); await load(); }
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    const ids = [...selected];
    if (!window.confirm(isPt
      ? `Excluir ${ids.length} leads?`
      : `Delete ${ids.length} leads?`)) return;
    try {
      const r = await axios.post(`${API}/leads-finder/leads/bulk-delete`, { lead_ids: ids });
      toast.success(isPt
        ? `${r.data.modified} leads excluídos · Desfazer`
        : `${r.data.modified} leads deleted · Undo`, {
        duration: 10000,
        action: {
          label: isPt ? "Desfazer" : "Undo",
          onClick: async () => {
            await Promise.all(ids.map((i) =>
              axios.post(`${API}/leads-finder/leads/${i}/restore`)));
            await load();
          },
        },
      });
      await load();
    } catch { toast.error("Bulk delete failed"); }
  };

  const filteredLeadsCount = leads.length;

  return (
    <div data-testid="lf-enrich-tab">
      <div className="al-card p-4 grid md:grid-cols-3 gap-3 mb-3">
        <MultiSelectCombo
          label={isPt ? "Cidade" : "City"}
          options={(tax.cities || []).map((c) => ({ label: c.label, count: c.count }))}
          values={cityFilter} onChange={(v) => setCityFilter(v.slice(-1))}
          isPt={isPt} testid="lfe-city"
          placeholder={isPt ? "Todas" : "All"}
        />
        <MultiSelectCombo
          label={isPt ? "Segmento" : "Industry"}
          options={(tax.industries || []).map((i) => ({ label: i.label, count: i.count }))}
          values={industryFilter} onChange={(v) => setIndustryFilter(v.slice(-1))}
          isPt={isPt} testid="lfe-industry"
          placeholder={isPt ? "Todos" : "All"}
        />
        <div>
          <label className="text-[10.5px] font-bold uppercase text-[var(--al-text-muted)] block mb-1">
            {isPt ? "Empresa (busca)" : "Company (search)"}
          </label>
          <input value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
            placeholder={isPt ? "Nome contém…" : "Name contains…"}
            className="al-input h-9 w-full text-[12px]"
            data-testid="lfe-company" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-[12.5px] text-[var(--al-text-muted)]" data-testid="lfe-total">
            {isPt
              ? `${filteredLeadsCount} de ${total} CRM leads sem e-mail`
              : `${filteredLeadsCount} of ${total} CRM leads without email`}
          </p>
          {/* Iter331 · Agrupar por */}
          <div className="inline-flex items-center gap-1 text-[10.5px]" data-testid="lfe-groupby">
            <span className="text-[var(--al-text-muted)] uppercase tracking-wider font-bold">
              {isPt ? "Agrupar por" : "Group by"}:
            </span>
            {[
              { id: "none",     label: isPt ? "Nenhum" : "None" },
              { id: "city",     label: isPt ? "Cidade" : "City" },
              { id: "industry", label: isPt ? "Segmento" : "Industry" },
              { id: "company",  label: isPt ? "Empresa" : "Company" },
            ].map((opt) => (
              <button key={opt.id} type="button"
                data-testid={`lfe-groupby-${opt.id}`}
                onClick={() => setGroupBy(opt.id)}
                className={`px-2 h-6 rounded border text-[10.5px] font-semibold transition ${
                  groupBy === opt.id
                    ? "bg-[#1B3A8A] border-[#1B3A8A] text-white"
                    : "border-[var(--al-border)] hover:border-[#1B3A8A] text-[var(--al-text-muted)]"}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <>
              <span className="text-[11.5px] text-[var(--al-text-muted)]"
                    data-testid="lfe-selected-count">
                {selected.size} {isPt ? "selecionados" : "selected"}
              </span>
              <button type="button" onClick={bulkDelete}
                data-testid="lfe-bulk-delete"
                className="h-9 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" />
                {isPt ? `Excluir ${selected.size}` : `Delete ${selected.size}`}
              </button>
            </>
          )}
          <button type="button" onClick={() => enrichBatch(10)} disabled={enriching}
            data-testid="lfe-enrich-10"
            className="h-9 px-3 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[11.5px] font-bold disabled:opacity-50">
            {isPt ? "Enriquecer 10" : "Enrich 10"}
          </button>
          <button type="button" onClick={() => enrichBatch(20)} disabled={enriching}
            data-testid="lfe-enrich-20"
            className="h-9 px-3 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[11.5px] font-bold disabled:opacity-50">
            {isPt ? "Enriquecer 20" : "Enrich 20"}
          </button>
          <div className="flex items-center gap-1">
            <input type="number" min={21} max={total || 500} value={customCount}
              onChange={(e) => setCustomCount(Math.max(21, parseInt(e.target.value || "21", 10)))}
              data-testid="lfe-enrich-custom-input"
              className="al-input h-9 w-16 text-[11.5px] text-center" />
            <button type="button" onClick={() => enrichBatch(customCount)} disabled={enriching}
              data-testid="lfe-enrich-custom-btn"
              className="h-9 px-2 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[11px] font-bold disabled:opacity-50">
              20+
            </button>
            <button type="button" onClick={() => enrichBatch(filteredLeadsCount)} disabled={enriching}
              data-testid="lfe-enrich-all"
              className="h-9 px-2 rounded-lg border border-[var(--al-border)] text-[10.5px] hover:bg-[var(--al-card)]"
              title={isPt ? "Enriquecer todos os filtrados" : "Enrich all filtered"}>
              {isPt ? "Todos" : "All"}
            </button>
          </div>
          <ExportButton
            source="leads-finder"
            testidPrefix="lf-export"
            filters={{
              q: companyFilter,
              segment: industryFilter?.length === 1 ? industryFilter[0] : "",
              city: cityFilter?.length === 1 ? cityFilter[0] : "",
            }}
          />
        </div>
      </div>

      {enriching && (
        <div className="al-card p-3 mb-3 flex items-center gap-3" data-testid="lfe-progress">
          <Loader2 className="w-4 h-4 animate-spin text-[#C9A24E]" />
          <div className="text-[12px] flex-1">
            {progress.done}/{progress.target} {isPt ? "enriquecidos" : "enriched"}
            <div className="w-full h-1 bg-[var(--al-border)] rounded mt-1 overflow-hidden">
              <div className="h-full bg-[#C9A24E] transition-all"
                style={{ width: `${(progress.done / Math.max(progress.target, 1)) * 100}%` }} />
            </div>
          </div>
        </div>
      )}

      {loading ? <Skeleton /> : groupBy === "none" ? (
        <div className="al-card overflow-x-auto">
          <table className="w-full text-[12px] min-w-[820px]">
            <thead>
              <tr className="border-b border-[var(--al-border)] text-left text-[10px] uppercase text-[var(--al-text-muted)]">
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox"
                    checked={leads.length > 0 && selected.size === leads.length}
                    onChange={toggleAll}
                    data-testid="lfe-select-all"
                    className="accent-[#C9A24E]" />
                </th>
                <th className="px-3 py-2.5">{isPt ? "Empresa" : "Company"}</th>
                <th className="px-3 py-2.5">{isPt ? "Segmento" : "Segment"}</th>
                <th className="px-3 py-2.5">{isPt ? "Local" : "Location"}</th>
                <th className="px-3 py-2.5">Website</th>
                <th className="px-3 py-2.5">{isPt ? "Resultado" : "Result"}</th>
                <th className="px-3 py-2.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <LeadRow key={l.id} l={l} index={i} isPt={isPt}
                  selected={selected} toggle={toggle}
                  results={results} enrichBatch={enrichBatch}
                  softDeleteOne={softDeleteOne}
                  industries={tax.industries || []}
                  onReclassify={reclassify} />
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[var(--al-text-muted)]">
                  {isPt ? "Nenhum lead corresponde aos filtros" : "No leads match the filters"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-2" data-testid="lfe-groups">
          {groupsLoading && <Skeleton />}
          {!groupsLoading && groups.length === 0 && (
            <div className="al-card p-6 text-center text-[12px] text-[var(--al-text-muted)]">
              {isPt ? "Nenhum grupo" : "No groups"}
            </div>
          )}
          {!groupsLoading && groups.map((g) => {
            const isOpen = !!openGroups[g.key];
            return (
              <div key={g.key} className="al-card overflow-hidden"
                   data-testid={`lfe-group-${g.key.replace(/[^a-z0-9]/gi, "_")}`}>
                <button type="button"
                  onClick={() => setOpenGroups((p) => ({ ...p, [g.key]: !p[g.key] }))}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--al-bg)]"
                  data-testid={`lfe-group-toggle-${g.key.replace(/[^a-z0-9]/gi, "_")}`}>
                  <div className="inline-flex items-center gap-2">
                    {isOpen
                      ? <ChevronDown className="w-4 h-4 text-[var(--al-text-muted)]" />
                      : <ChevronRight className="w-4 h-4 text-[var(--al-text-muted)]" />}
                    <span className="font-bold text-[13px]">{g.key}</span>
                    <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#1B3A8A]/10 text-[#1B3A8A] border border-[#1B3A8A]/30">
                      {g.count} {isPt ? "leads" : "leads"}
                    </span>
                    {g.with_email > 0 && (
                      <span className="text-[10.5px] text-emerald-600 inline-flex items-center gap-0.5">
                        <Mail className="w-3 h-3" /> {g.with_email}
                      </span>
                    )}
                    {g.unclassified > 0 && (
                      <span className="text-[10.5px] text-amber-500 inline-flex items-center gap-0.5"
                            title={isPt ? "Sem segmento" : "No segment"}>
                        <AlertTriangle className="w-3 h-3" /> {g.unclassified}
                      </span>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="overflow-x-auto border-t border-[var(--al-border)]">
                    <table className="w-full text-[12px] min-w-[820px]">
                      <tbody>
                        {g.items.map((l, i) => (
                          <LeadRow key={l.id} l={l} index={i} isPt={isPt}
                            selected={selected} toggle={toggle}
                            results={results} enrichBatch={enrichBatch}
                            softDeleteOne={softDeleteOne}
                            industries={tax.industries || []}
                            onReclassify={reclassify}
                            compact />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Row (shared between flat and grouped view) ───────────────────────

function LeadRow({ l, index, isPt, selected, toggle, results,
                    enrichBatch, softDeleteOne, industries, onReclassify,
                    compact = false }) {
  const r = results[l.id];
  const currentSeg = l.industry || l.sector || "";
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  return (
    <tr data-testid={`lfe-row-${index}`}
        className="border-b border-[var(--al-border)] last:border-0 hover:bg-[var(--al-bg)]">
      <td className="px-3 py-2.5 w-8">
        <input type="checkbox" checked={selected.has(l.id)}
          onChange={() => toggle(l.id)}
          data-testid={`lfe-row-check-${l.id}`}
          className="accent-[#C9A24E]" />
      </td>
      <td className="px-3 py-2.5 font-semibold">
        {l.name}
        {compact && (
          <div className="text-[10.5px] text-[var(--al-text-muted)] font-normal">
            {l.city}{l.state ? `, ${l.state}` : ""}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <select autoFocus defaultValue={currentSeg}
              onBlur={() => setEditing(false)}
              onChange={async (e) => {
                const v = e.target.value;
                setPending(true);
                await onReclassify(l, v);
                setPending(false);
                setEditing(false);
              }}
              data-testid={`lfe-reclassify-select-${l.id}`}
              className="al-input h-7 text-[11px] py-0 pr-1">
              <option value="">—</option>
              {industries.map((i) => (
                <option key={i.label} value={i.label}>{i.label}</option>
              ))}
            </select>
            {pending && <Loader2 className="w-3 h-3 animate-spin text-[#C9A24E]" />}
          </div>
        ) : (
          <button type="button"
            onClick={() => setEditing(true)}
            data-testid={`lfe-reclassify-btn-${l.id}`}
            title={isPt ? "Reclassificar segmento" : "Reclassify segment"}
            className="inline-flex items-center gap-1 text-[11px] px-1.5 h-6 rounded border border-transparent hover:border-[#C9A24E] hover:bg-[#C9A24E]/10 text-[var(--al-text)]">
            <Tag className="w-3 h-3 text-[var(--al-text-muted)]" />
            <span className={currentSeg ? "" : "italic text-[var(--al-text-muted)]"}>
              {currentSeg || (isPt ? "sem segmento" : "no segment")}
            </span>
          </button>
        )}
      </td>
      {!compact && (
        <td className="px-3 py-2.5 text-[var(--al-text-muted)]">
          {l.city}{l.state ? `, ${l.state}` : ""}
        </td>
      )}
      <td className="px-3 py-2.5 text-[var(--al-text-muted)] truncate max-w-[160px]">
        {l.website || "—"}
      </td>
      <td className="px-3 py-2.5">
        {r?.status === "enriched" && (
          <span className="text-emerald-600 font-semibold inline-flex items-center gap-1"
                data-testid={`lfe-result-ok-${l.id}`}>
            <Mail className="w-3 h-3" /> {r.email}
            <span className="text-[10px] text-[var(--al-text-muted)]">({r.contact_name})</span>
          </span>
        )}
        {r?.status === "no_email_found" && (
          <span className="text-amber-500 inline-flex items-center gap-1"
                data-testid={`lfe-result-warn-${l.id}`}>
            <AlertTriangle className="w-3 h-3" /> {isPt ? "Não encontrado" : "Not found"}
          </span>
        )}
        {r?.status === "apify_error" && (
          <button onClick={() => enrichBatch(1)}
                  data-testid={`lfe-retry-${l.id}`}
                  className="text-rose-500 text-[10.5px] underline">
            {isPt ? "Erro · Tentar novamente" : "Error · Retry"}
          </button>
        )}
        {!r && l.lf_enrich_status === "enriched" && (
          <span className="text-emerald-600 text-[10.5px]">✓ {isPt ? "já enriquecido" : "already enriched"}</span>
        )}
        {!r && l.lf_enrich_status === "no_email_found" && (
          <span className="text-[var(--al-text-muted)] text-[10.5px]">
            {isPt ? "tentado, sem e-mail" : "tried, no email"}
          </span>
        )}
        {l.email && !r && l.lf_enrich_status !== "enriched" && (
          <span className="text-emerald-600 text-[10.5px] inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> {l.email}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5">
        <button type="button" onClick={() => softDeleteOne(l)}
          data-testid={`lfe-del-${l.id}`}
          title={isPt ? "Excluir lead" : "Delete lead"}
          className="w-6 h-6 rounded bg-red-500/10 hover:bg-red-600 text-red-500 hover:text-white flex items-center justify-center">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function safeGet(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v || fallback; }
  catch { return fallback; }
}
function safeSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}
